import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Plus, Trash2, CheckCircle, XCircle, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [codes, setCodes] = useState([]);
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newCode, setNewCode] = useState({ code: "", amount: "" });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [codesRes, claimsRes] = await Promise.all([
        axios.get(`${API}/admin/codes`),
        axios.get(`${API}/admin/claims`)
      ]);
      setCodes(codesRes.data);
      setClaims(claimsRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleAddCode = async () => {
    if (!newCode.code || !newCode.amount) {
      toast.error("Please fill in all fields");
      return;
    }

    try {
      await axios.post(`${API}/admin/codes`, {
        code: newCode.code.toUpperCase(),
        amount: parseFloat(newCode.amount)
      });
      toast.success("Code added successfully!");
      setNewCode({ code: "", amount: "" });
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add code");
    }
  };

  const handleDeleteCode = async (code) => {
    if (!window.confirm(`Delete code ${code}?`)) return;

    try {
      await axios.delete(`${API}/admin/codes/${code}`);
      toast.success("Code deleted successfully");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete code");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-pink-50 to-purple-50 flex items-center justify-center">
        <div className="text-2xl font-bold text-primary">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-pink-50 to-purple-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Button
            data-testid="back-to-home-button"
            onClick={() => navigate("/")}
            variant="outline"
            className="mb-4 rounded-full"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
          <h1 className="font-heading text-4xl md:text-5xl text-primary uppercase mb-2">
            Admin Dashboard
          </h1>
          <p className="text-foreground/70 text-lg">Manage claim codes and view submissions</p>
        </motion.div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <motion.div
            className="bg-white rounded-2xl p-6 shadow-xl border-2 border-purple-100"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <h3 className="text-foreground/60 font-bold mb-2">Total Codes</h3>
            <p className="font-heading text-4xl text-primary">{codes.length}</p>
          </motion.div>
          <motion.div
            className="bg-white rounded-2xl p-6 shadow-xl border-2 border-purple-100"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <h3 className="text-foreground/60 font-bold mb-2">Total Claims</h3>
            <p className="font-heading text-4xl text-accent">{claims.length}</p>
          </motion.div>
          <motion.div
            className="bg-white rounded-2xl p-6 shadow-xl border-2 border-purple-100"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <h3 className="text-foreground/60 font-bold mb-2">Available Codes</h3>
            <p className="font-heading text-4xl text-green-600">
              {codes.filter(c => !c.claimed).length}
            </p>
          </motion.div>
        </div>

        {/* Codes Section */}
        <motion.div
          className="bg-white rounded-3xl p-6 md:p-8 shadow-xl mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <div className="flex justify-between items-center mb-6">
            <h2 className="font-heading text-2xl text-primary uppercase">Claim Codes</h2>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-code-button" className="rounded-full bg-primary hover:bg-primary/90">
                  <Plus className="w-5 h-5 mr-2" />
                  Add Code
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add New Claim Code</DialogTitle>
                  <DialogDescription>
                    Create a new claim code with prize amount
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div>
                    <Label htmlFor="code">Claim Code</Label>
                    <Input
                      data-testid="new-code-input"
                      id="code"
                      value={newCode.code}
                      onChange={(e) => setNewCode({ ...newCode, code: e.target.value.toUpperCase() })}
                      placeholder="MEGA-12345"
                      className="uppercase"
                    />
                  </div>
                  <div>
                    <Label htmlFor="amount">Prize Amount ($)</Label>
                    <Input
                      data-testid="new-amount-input"
                      id="amount"
                      type="number"
                      value={newCode.amount}
                      onChange={(e) => setNewCode({ ...newCode, amount: e.target.value })}
                      placeholder="5500000"
                    />
                  </div>
                  <Button
                    data-testid="create-code-button"
                    onClick={handleAddCode}
                    className="w-full rounded-full bg-primary hover:bg-primary/90"
                  >
                    Create Code
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-purple-100">
                  <th className="text-left p-3 font-bold text-primary">Code</th>
                  <th className="text-left p-3 font-bold text-primary">Amount</th>
                  <th className="text-left p-3 font-bold text-primary">Status</th>
                  <th className="text-left p-3 font-bold text-primary">Actions</th>
                </tr>
              </thead>
              <tbody>
                {codes.map((code) => (
                  <tr key={code.code_id} className="border-b border-purple-50" data-testid="code-row">
                    <td className="p-3 font-mono font-bold">{code.code}</td>
                    <td className="p-3 font-bold text-lg">${code.amount.toLocaleString()}</td>
                    <td className="p-3">
                      {code.claimed ? (
                        <span className="flex items-center gap-2 text-red-600">
                          <XCircle className="w-5 h-5" />
                          Claimed
                        </span>
                      ) : (
                        <span className="flex items-center gap-2 text-green-600">
                          <CheckCircle className="w-5 h-5" />
                          Available
                        </span>
                      )}
                    </td>
                    <td className="p-3">
                      <Button
                        data-testid="delete-code-button"
                        onClick={() => handleDeleteCode(code.code)}
                        variant="destructive"
                        size="sm"
                        className="rounded-full"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {codes.length === 0 && (
              <p className="text-center py-8 text-foreground/50">No codes yet. Add one to get started!</p>
            )}
          </div>
        </motion.div>

        {/* Claims Section */}
        <motion.div
          className="bg-white rounded-3xl p-6 md:p-8 shadow-xl"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <h2 className="font-heading text-2xl text-primary uppercase mb-6">Recent Claims</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-purple-100">
                  <th className="text-left p-3 font-bold text-primary">Name</th>
                  <th className="text-left p-3 font-bold text-primary">Email</th>
                  <th className="text-left p-3 font-bold text-primary">Phone</th>
                  <th className="text-left p-3 font-bold text-primary">Amount</th>
                  <th className="text-left p-3 font-bold text-primary">Code</th>
                  <th className="text-left p-3 font-bold text-primary">Date</th>
                </tr>
              </thead>
              <tbody>
                {claims.map((claim) => (
                  <tr key={claim.claim_id} className="border-b border-purple-50" data-testid="claim-row">
                    <td className="p-3 font-bold">{claim.name}</td>
                    <td className="p-3">{claim.email}</td>
                    <td className="p-3">{claim.phone || 'N/A'}</td>
                    <td className="p-3 font-bold text-lg text-accent">${claim.amount.toLocaleString()}</td>
                    <td className="p-3 font-mono">{claim.code}</td>
                    <td className="p-3">{new Date(claim.claimed_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {claims.length === 0 && (
              <p className="text-center py-8 text-foreground/50">No claims submitted yet.</p>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}